// src/components/admin/tabs/ProjectsTab.jsx
// 项目检索：项目表 + 全链路详情（任务 / 错误聚合 / 费用），支持改状态、归档/恢复、
// 改创建人、以管理员进入项目。
// 从 AdminDashboard.jsx 抽取：业务逻辑、API 调用与 confirm/prompt 流程逐字保留，仅重构展示与轻交互。
import { appPrompt } from '../../../services/appDialogs';
import { AppSelect } from '../../ui/AppSelect';
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ExternalLink, UserCog } from "lucide-react";
import {
  fetchProjects,
  fetchProjectFullTrace,
  updateAdminProjectStatus,
  updateAdminProjectOwner,
  archiveAdminProject,
  restoreAdminProject,
} from "../../../services/adminService";
import {
  Panel,
  StatCard,
  EmptyText,
  ErrorNotice,
  FilterInput,
  FilterSelect,
  PrimaryButton,
  SecondaryButton,
  StatusBadge,
} from "../adminUi.jsx";
import {
  displayUserId,
  formatCostSpec,
  shortText,
  formatDetailedMoney,
} from "../adminUtils";

const ACCENT = "var(--color-warning)";

const PROJECT_STATUS_LABELS = {
  production: '制作中',
  online: '交付上线',
  delayed: '延期',
  archived: '已归档',
  deleted: '已删除',
};

// 状态徽章语义色：制作中=info、交付上线=success、延期=error、已归档/已删除=muted
const PROJECT_STATUS_COLORS = {
  production: 'var(--color-info)',
  online: 'var(--color-success)',
  delayed: 'var(--color-error)',
  archived: 'var(--color-text-muted)',
  deleted: 'var(--color-text-muted)',
};

const INLINE_SELECT_STYLE = {
  backgroundColor: 'var(--color-bg-tertiary)',
  color: 'var(--color-text-primary)',
  border: '1px solid var(--color-border-primary)',
};

// eslint-disable-next-line no-unused-vars -- 统一 tab props 签名，projectId/projectOptions 本 tab 不使用
const ProjectsTab = ({ projectId, projectOptions, onProjectSelect, selectedProjectId, onProjectsChanged }) => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState({ keyword: '', userId: '', status: '', limit: 500 });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [projectTrace, setProjectTrace] = useState(null);
  const [traceLoading, setTraceLoading] = useState(false);
  const [ownerUpdatingId, setOwnerUpdatingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setRows(await fetchProjects(filters));
    } catch (e) {
      console.error(e);
      setError(e?.message || '项目列表加载失败');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const openProject = async (row) => {
    onProjectSelect(String(row.id));
    setProjectTrace(null);
    setTraceLoading(true);
    try {
      setProjectTrace(await fetchProjectFullTrace(row.id));
    } catch (e) {
      console.error(e);
      setError(e?.message || '项目详情加载失败');
    } finally {
      setTraceLoading(false);
    }
  };

  const changeProjectStatus = async (row, status) => {
    setError('');
    try {
      if (status === 'archived') {
        await archiveAdminProject(row.id);
      } else if (row.status === 'archived' || row.status === 'deleted') {
        await restoreAdminProject(row.id, status);
      } else {
        await updateAdminProjectStatus(row.id, status);
      }
      await load();
      if (projectTrace?.project?.id === row.id || String(selectedProjectId) === String(row.id)) {
        setProjectTrace(await fetchProjectFullTrace(row.id));
      }
    } catch (e) {
      console.error(e);
      setError(e?.message || '项目状态更新失败');
    }
  };

  const enterProject = (row) => {
    if (!row?.id) return;
    navigate(`/project/${row.id}`);
  };

  const changeProjectOwner = async (row) => {
    const currentOwner = displayUserId(row.userId, '');
    const nextOwner = (await appPrompt(`将项目「${row.name || row.id}」的创建人改为：`, currentOwner));
    const normalized = nextOwner == null ? '' : String(nextOwner).trim();
    if (!normalized || normalized === currentOwner) return;
    setError('');
    setOwnerUpdatingId(row.id);
    try {
      await updateAdminProjectOwner(row.id, normalized);
      await load();
      await onProjectsChanged?.();
      if (projectTrace?.project?.id === row.id || String(selectedProjectId) === String(row.id)) {
        setProjectTrace(await fetchProjectFullTrace(row.id));
      }
    } catch (e) {
      console.error(e);
      setError(e?.message || '项目创建人更新失败');
    } finally {
      setOwnerUpdatingId(null);
    }
  };

  return (
    <div className="space-y-4">
      {/* 筛选条 */}
      <div
        className="rounded-lg border px-4 py-3 flex flex-wrap gap-3 items-end"
        style={{ backgroundColor: 'var(--color-bg-secondary)', borderColor: 'var(--color-border-secondary)' }}
      >
        <FilterInput label="关键词" value={filters.keyword} onChange={(v) => setFilters(f => ({ ...f, keyword: v }))} />
        <FilterInput label="创建人" value={filters.userId} onChange={(v) => setFilters(f => ({ ...f, userId: v }))} />
        <FilterSelect label="状态" value={filters.status} options={['', 'production', 'online', 'delayed', 'archived', 'deleted']} onChange={(v) => setFilters(f => ({ ...f, status: v }))} />
        <FilterInput label="条数" type="number" value={filters.limit} onChange={(v) => setFilters(f => ({ ...f, limit: v }))} />
        <PrimaryButton onClick={load} disabled={loading}>
          {loading ? '查询中…' : '查询'}
        </PrimaryButton>
      </div>
      {error && <ErrorNotice message={error} />}

      {/* 项目表 */}
      <Panel
        title="项目检索"
        sub="点击项目打开链路详情，并作为全局过滤条件"
        right={<span className="font-mono-num text-2xs" style={{ color: 'var(--color-text-muted)' }}>{loading ? '加载中…' : `${rows.length} 个项目`}</span>}
      >
        <div className="text-xs mb-3" style={{ color: 'var(--color-text-muted)' }}>
          点击项目会打开链路详情，并作为全局过滤条件，概览、用户、AI任务、错误、费用和日志都会按该项目收敛。
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-label" style={{ textAlign: 'left' }}>
                {['ID', '项目', '创建人', '状态', '截止日期', '创建时间', '简介', '后台操作'].map(h => (
                  <th key={h} className="font-medium pb-2.5 pr-4">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && !rows.length ? (
                <tr>
                  <td colSpan={8} className="py-3">
                    <div className="space-y-2">
                      {[0, 1, 2, 3, 4].map(i => <div key={i} className="skeleton h-7" />)}
                    </div>
                  </td>
                </tr>
              ) : !rows.length ? (
                <tr><td colSpan={8}><EmptyText text="暂无项目" /></td></tr>
              ) : rows.map(row => {
                const active = String(row.id) === String(selectedProjectId);
                return (
                  <tr key={row.id}
                    onClick={() => openProject(row)}
                    className="transition-colors duration-150"
                    style={{
                      borderTop: '1px solid var(--color-border-secondary)',
                      cursor: 'pointer',
                      backgroundColor: active ? 'color-mix(in srgb, var(--color-warning) 8%, transparent)' : 'transparent',
                    }}>
                    <td className="py-2 pr-4 font-mono-num text-xs" style={active ? { boxShadow: 'inset 2px 0 0 var(--color-warning)' } : undefined}>{row.id}</td>
                    <td className="py-2 pr-4 text-xs font-semibold" style={{ color: 'var(--color-text-primary)' }}>{row.name || '-'}</td>
                    <td className="py-2 pr-4 text-xs">{displayUserId(row.userId)}</td>
                    <td className="py-2 pr-4">
                      <StatusBadge
                        label={PROJECT_STATUS_LABELS[row.status] || row.status || '-'}
                        color={PROJECT_STATUS_COLORS[row.status]}
                      />
                    </td>
                    <td className="py-2 pr-4 text-xs font-mono-num">{row.deadline || '-'}</td>
                    <td className="py-2 pr-4 text-2xs font-mono-num" style={{ color: 'var(--color-text-muted)' }}>{row.createdAt ? new Date(row.createdAt).toLocaleString() : '-'}</td>
                    <td className="py-2 pr-4 text-xs max-w-sm truncate" style={{ color: 'var(--color-text-secondary)' }}>{row.description || '-'}</td>
                    <td className="py-2 pr-4" onClick={(e) => e.stopPropagation()}>
                      <div className="flex flex-wrap items-center gap-2">
                        <AppSelect
                          value={row.status || 'production'}
                          onChange={(e) => changeProjectStatus(row, e.target.value)}
                          className="h-7 px-2 rounded-md text-xs outline-none"
                          style={INLINE_SELECT_STYLE}
                        >
                          {Object.entries(PROJECT_STATUS_LABELS).map(([value, label]) => (
                            <option key={value} value={value}>{label}</option>
                          ))}
                        </AppSelect>
                        <span title="以管理员身份进入项目" className="inline-flex">
                          <SecondaryButton
                            onClick={() => enterProject(row)}
                            className="!h-7 !px-2.5 inline-flex items-center gap-1"
                          >
                            <ExternalLink size={12} />
                            进入
                          </SecondaryButton>
                        </span>
                        <span title="修改项目创建人" className="inline-flex">
                          <SecondaryButton
                            disabled={ownerUpdatingId === row.id}
                            onClick={() => changeProjectOwner(row)}
                            className="!h-7 !px-2.5 inline-flex items-center gap-1"
                          >
                            <UserCog size={12} />
                            {ownerUpdatingId === row.id ? '修改中' : '改创建人'}
                          </SecondaryButton>
                        </span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Panel>

      {/* 项目链路详情 */}
      {(traceLoading || projectTrace) && (
        <Panel
          title="项目链路详情"
          sub={projectTrace?.project?.name || (traceLoading ? '加载中…' : '-')}
          right={<SecondaryButton onClick={() => setProjectTrace(null)}>关闭</SecondaryButton>}
        >
          {traceLoading || !projectTrace ? (
            <div className="space-y-3">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {[0, 1, 2, 3, 4].map(i => <div key={i} className="skeleton h-16" />)}
              </div>
              <div className="skeleton h-40" />
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <StatCard label="AI任务" value={projectTrace.taskCounts?.total ?? 0} />
                <StatCard label="运行中" value={projectTrace.taskCounts?.processing ?? 0} />
                <StatCard label="失败" value={projectTrace.taskCounts?.failed ?? 0} />
                <StatCard label="费用" value={`¥${projectTrace.costs?.totalCost?.toFixed?.(2) ?? projectTrace.costs?.totalCost ?? 0}`} color={ACCENT} />
                <StatCard label="成员" value={projectTrace.members?.length ?? 0} />
              </div>

              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                <div className="p-1">
                  <div className="text-[13px] font-semibold mb-3" style={{ color: 'var(--color-text-primary)' }}>最近 AI 任务</div>
                  <div className="space-y-2 max-h-72 overflow-y-auto custom-scrollbar">
                    {!(projectTrace.latestTasks || []).length ? <EmptyText text="暂无任务" /> : projectTrace.latestTasks.map(task => (
                      <div key={task.internalTaskId || task.internal_task_id} className="rounded-md p-2 text-xs" style={{ backgroundColor: 'var(--color-bg-primary)' }}>
                        <div className="font-mono-num break-all" style={{ color: 'var(--color-info)' }}>{task.internalTaskId || task.internal_task_id}</div>
                        <div>{task.type || '-'} · {task.status || '-'} · {task.source || '-'}</div>
                        <div className="font-mono-num text-2xs" style={{ color: 'var(--color-text-muted)' }}>{task.createdAt || task.created_at ? new Date(task.createdAt || task.created_at).toLocaleString() : '-'}</div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="p-1">
                  <div className="text-[13px] font-semibold mb-3" style={{ color: 'var(--color-text-primary)' }}>错误聚合</div>
                  <div className="space-y-2 max-h-72 overflow-y-auto custom-scrollbar">
                    {!(projectTrace.errorGroups?.groups || []).length ? <EmptyText text="暂无错误" /> : projectTrace.errorGroups.groups.map(group => (
                      <div key={group.key} className="rounded-md p-2 text-xs" style={{ backgroundColor: 'var(--color-red-light)', border: '1px solid rgba(239,68,68,0.3)' }}>
                        <div className="font-mono-num text-xs font-semibold" style={{ color: 'var(--color-error)' }}>{group.count} 次</div>
                        <div className="break-words">{shortText(group.summary, 180)}</div>
                        <div className="font-mono-num text-2xs mt-1" style={{ color: 'var(--color-text-muted)' }}>{group.sampleTaskId || '-'}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="p-1">
                <div className="text-[13px] font-semibold mb-3" style={{ color: 'var(--color-text-primary)' }}>最近费用</div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-label" style={{ textAlign: 'left' }}>
                        {['时间', '类型', '来源', '模型', '规格', '费用', '任务ID'].map(h => <th key={h} className="font-medium pb-2.5 pr-4">{h}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {!(projectTrace.costs?.rows || []).length ? (
                        <tr><td colSpan={7}><EmptyText text="暂无费用记录" /></td></tr>
                      ) : projectTrace.costs.rows.map(row => (
                        <tr key={row.id} style={{ borderTop: '1px solid var(--color-border-secondary)' }}>
                          <td className="py-2 pr-4 text-2xs font-mono-num" style={{ color: 'var(--color-text-muted)' }}>{row.createdAt ? new Date(row.createdAt).toLocaleString() : '-'}</td>
                          <td className="py-2 pr-4 text-xs">{row.type || '-'}</td>
                          <td className="py-2 pr-4 text-xs">{shortText(row.source, 32)}</td>
                          <td className="py-2 pr-4 text-xs">{shortText(`${row.model || '-'} / ${row.subModel || '-'}`, 40)}</td>
                          <td className="py-2 pr-4 text-2xs font-mono-num" style={{ color: 'var(--color-text-muted)' }}>{formatCostSpec(row)}</td>
                          <td className="py-2 pr-4 text-xs font-mono-num font-semibold" style={{ color: ACCENT }}>{formatDetailedMoney(row.cost)}</td>
                          <td className="py-2 pr-4 font-mono-num text-2xs">{row.internalTaskId || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </Panel>
      )}
    </div>
  );
};

export default ProjectsTab;
